import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api, { errMsg, saveAuth } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Check, Gift, BookOpen, Sparkles, Copy, ArrowRight, Lock, AlertCircle } from "lucide-react";

/**
 * Post-check-in onboarding page.
 *
 * Rendered when the guest opens the link from the welcome email or the QR shown at the reception desk.
 * Uses the same `self_checkin_token` as SelfCheckin.jsx — the token is reused to identify the booking
 * and the same POST /api/self-checkin/{token}/register-account endpoint issues the 5% welcome promo.
 */
export default function Welcome() {
  const { token } = useParams();
  const { lang } = useLang();
  const IT = lang === "it";
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [password, setPassword] = useState("");
  const [registering, setRegistering] = useState(false);
  const [promo, setPromo] = useState(null);
  const [accountCreated, setAccountCreated] = useState(false);

  useEffect(() => {
    api.get(`/self-checkin/${token}`)
      .then((r) => setInfo(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">{IT ? "Caricamento…" : "Loading…"}</div>;
  if (notFound) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-3"/>
        <h1 className="font-display text-2xl mb-2">{IT ? "Link non valido" : "Invalid link"}</h1>
        <p className="text-sm text-muted-foreground">{IT ? "Questo link di benvenuto non è più attivo. Contatta la tua struttura." : "This welcome link is no longer active. Contact your host."}</p>
      </div>
    </div>
  );

  const guestEmail = info?.booking?.guest_email || "";
  const guestName = info?.booking?.guest_name || "";
  const propertyName = info?.property?.name || "";
  const canRegister = info?.account_status === "none" && !!guestEmail;
  const alreadyGuest = info?.account_status === "guest";
  const otherRole = info?.account_status === "other_role";

  const doRegister = async () => {
    if (!password || password.length < 8) {
      toast.error(IT ? "La password deve essere di almeno 8 caratteri" : "Password must be at least 8 characters");
      return;
    }
    setRegistering(true);
    try {
      const r = await api.post(`/self-checkin/${token}/register-account`, { password, marketing_opt_in: true });
      saveAuth(r.data.token, r.data.user);
      setPromo(r.data.promo_code);
      setAccountCreated(true);
      toast.success(IT ? "Account creato! Sconto attivato." : "Account created! Discount unlocked.");
    } catch (e) {
      toast.error(errMsg(e));
    } finally { setRegistering(false); }
  };

  const copyCode = async () => {
    if (!promo?.code) return;
    try {
      await navigator.clipboard.writeText(promo.code);
      toast.success(IT ? "Codice copiato" : "Code copied");
    } catch { toast.error(IT ? "Impossibile copiare" : "Copy failed"); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-sand/40 to-background px-4 sm:px-6 py-10 sm:py-14">
      <div className="max-w-xl mx-auto space-y-6" data-testid="welcome-page">
        {/* Success card */}
        <div className="bg-card border border-primary/30 rounded-2xl p-8 text-center shadow-sm">
          <div className="w-16 h-16 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-sage"/>
          </div>
          <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-2">
            {IT ? "Check-in completato" : "Check-in complete"}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl mb-2">
            {IT ? `Benvenuta/o in ${propertyName}!` : `Welcome to ${propertyName}!`}
          </h1>
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {IT
              ? `Ciao ${guestName.split(" ")[0]}, il tuo soggiorno è confermato. Sblocca in 5 secondi i vantaggi HospitalityHub.`
              : `Hi ${guestName.split(" ")[0]}, your stay is confirmed. Unlock HospitalityHub perks in 5 seconds.`}
          </p>
        </div>

        {/* Promo code result (after account is created) */}
        {accountCreated && promo && (
          <div className="bg-primary/5 border-2 border-primary/40 rounded-2xl p-6 text-center" data-testid="welcome-promo-card">
            <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold flex items-center justify-center gap-1.5 mb-2">
              <Sparkles className="w-3 h-3"/>{IT ? "Il tuo sconto di benvenuto" : "Your welcome discount"}
            </div>
            <div className="font-display text-2xl mb-3">{IT ? `-${promo.percent_off}% sulla prossima prenotazione` : `-${promo.percent_off}% on your next booking`}</div>
            <button
              onClick={copyCode}
              className="inline-flex items-center gap-2 bg-card border border-dashed border-primary/60 rounded-lg px-4 py-3 font-mono text-lg tracking-widest text-primary hover:bg-primary/10 transition"
              data-testid="welcome-promo-copy"
            >
              {promo.code}<Copy className="w-4 h-4 opacity-60"/>
            </button>
            <p className="text-[11px] text-muted-foreground mt-2">{IT ? "Valido 6 mesi · uso singolo · ricevuto anche via email" : "Valid 6 months · single use · also sent by email"}</p>
            <Button
              onClick={() => nav("/guest/dashboard")}
              className="mt-4 bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="welcome-goto-dashboard"
            >
              {IT ? "Vai al mio pannello" : "Open my dashboard"}<ArrowRight className="w-4 h-4 ml-1"/>
            </Button>
          </div>
        )}

        {/* Registration invite */}
        {!accountCreated && canRegister && (
          <div className="bg-card border border-border rounded-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-primary/10 via-sage/10 to-primary/5 px-6 py-5">
              <div className="uppercase tracking-[0.2em] text-[10px] text-primary font-semibold flex items-center gap-1.5">
                <Gift className="w-3 h-3"/>{IT ? "Un regalo per te" : "A gift for you"}
              </div>
              <h2 className="font-display text-2xl mt-1">
                {IT ? "Sblocca i vantaggi HospitalityHub" : "Unlock HospitalityHub perks"}
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                {IT
                  ? "Crea il tuo account in 5 secondi — usiamo i dati che hai fornito al check-in."
                  : "Create your account in 5 seconds — we use the data you gave at check-in."}
              </p>
            </div>

            <div className="px-6 py-5 space-y-4">
              <ul className="space-y-2.5">
                <li className="flex items-start gap-3 text-sm" data-testid="welcome-benefit-discount">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Gift className="w-4 h-4 text-primary"/></div>
                  <div>
                    <div className="font-semibold">{IT ? "Sconto 5% sulla prossima prenotazione" : "5% off your next booking"}</div>
                    <div className="text-xs text-muted-foreground">{IT ? "Codice consegnato subito, valido 6 mesi in tutta la rete" : "Code issued instantly, valid 6 months across the network"}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm" data-testid="welcome-benefit-guide">
                  <div className="w-8 h-8 rounded-full bg-sage/15 flex items-center justify-center shrink-0"><BookOpen className="w-4 h-4 text-sage"/></div>
                  <div>
                    <div className="font-semibold">{IT ? "Guida digitale della struttura" : "Digital property guide"}</div>
                    <div className="text-xs text-muted-foreground">{IT ? "Orari, wifi, colazione, indicazioni sempre a portata di mano" : "Hours, wifi, breakfast, directions — always on hand"}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3 text-sm" data-testid="welcome-benefit-exclusive">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0"><Sparkles className="w-4 h-4 text-primary"/></div>
                  <div>
                    <div className="font-semibold">{IT ? "Contenuti ed esperienze esclusive" : "Exclusive content & experiences"}</div>
                    <div className="text-xs text-muted-foreground">{IT ? "Offerte partner, itinerari, consigli locali riservati agli iscritti" : "Partner offers, itineraries and local tips just for members"}</div>
                  </div>
                </li>
              </ul>

              <div className="pt-3 border-t border-border space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px]">{IT ? "Nome" : "Name"}</Label>
                    <Input value={guestName} readOnly disabled className="bg-muted/50" data-testid="welcome-reg-name"/>
                  </div>
                  <div>
                    <Label className="text-[11px]">Email</Label>
                    <Input value={guestEmail} readOnly disabled className="bg-muted/50 text-xs" data-testid="welcome-reg-email"/>
                  </div>
                </div>
                <div>
                  <Label className="text-[11px] flex items-center gap-1"><Lock className="w-3 h-3"/>{IT ? "Scegli una password (min. 8 caratteri)" : "Choose a password (min. 8 chars)"}</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={IT ? "••••••••" : "••••••••"}
                    autoComplete="new-password"
                    minLength={8}
                    data-testid="welcome-reg-password"
                  />
                </div>
                <Button
                  onClick={doRegister}
                  disabled={registering || password.length < 8}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift"
                  size="lg"
                  data-testid="welcome-register-btn"
                >
                  {registering ? "…" : (IT ? "Crea account e attiva lo sconto" : "Create account & unlock discount")}
                </Button>
                <p className="text-[10px] text-muted-foreground text-center">
                  {IT ? "Iscrivendoti accetti i Termini e la Privacy. Puoi disiscriverti in qualsiasi momento." : "By signing up you accept the Terms and Privacy. You can opt out any time."}
                </p>
              </div>
            </div>
          </div>
        )}

        {!accountCreated && alreadyGuest && (
          <div className="bg-card border border-border rounded-2xl p-6 text-center" data-testid="welcome-already-registered">
            <div className="uppercase tracking-[0.2em] text-[10px] text-sage font-semibold mb-2">{IT ? "Ben ritrovato!" : "Welcome back!"}</div>
            <p className="text-sm text-muted-foreground mb-4">
              {IT ? "Hai già un account HospitalityHub con questa email. Accedi per vedere le tue prenotazioni." : "You already have a HospitalityHub account with this email. Log in to see your bookings."}
            </p>
            <Link
              to={`/guest/auth?mode=login&email=${encodeURIComponent(guestEmail)}`}
              className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 rounded-md px-4 py-2 text-sm font-semibold"
              data-testid="welcome-login-link"
            >
              {IT ? "Accedi" : "Log in"}<ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        )}

        {!accountCreated && otherRole && (
          <div className="bg-card border border-border rounded-2xl p-5 text-center text-xs text-muted-foreground" data-testid="welcome-other-role">
            {IT
              ? "L'email della prenotazione è già registrata come struttura o partner. Usa il login principale per accedere."
              : "This booking email is already registered as a host or partner. Use the main login to sign in."}
          </div>
        )}

        <div className="text-center">
          <Link to="/" className="text-muted-foreground hover:text-primary text-xs">← {IT ? "Torna alla home" : "Back to home"}</Link>
        </div>
      </div>
    </div>
  );
}
